import React from 'react';
import { Button, Badge } from 'react-bootstrap';
import { Pencil, Trash2, Clock, Camera, FileText, Ruler } from 'lucide-react';
import {
    BuildingCheckTemplate,
    INTERVAL_UNITS,
    NEXT_DUE_DATE_OPTIONS,
    InspectionCategory
} from './types';
import { translateInspectionCategory, translateRegulatoryType } from '../../types/EnumTranslations';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { ASSESSMENT_GROUPS } from '../../Helpers/assessmentGroups';

interface BuildingCheckTemplateDetailPaneProps {
    template: BuildingCheckTemplate;
    onEdit: (template: BuildingCheckTemplate) => void;
    onDelete: (template: BuildingCheckTemplate) => void;
}

const BuildingCheckTemplateDetailPane: React.FC<BuildingCheckTemplateDetailPaneProps> = ({
    template,
    onEdit,
    onDelete
}) => {
    const { getTranslation } = useBuildComponentTypes();

    const getIntervalLabel = () => {
        const unitLabel = INTERVAL_UNITS.find(u => u.value === template.interval.unit)?.label.toLowerCase() || template.interval.unit;
        return `Var ${template.interval.value} ${unitLabel}`;
    };

    const getNextDueDateLabel = () => {
        return NEXT_DUE_DATE_OPTIONS.find(o => o.value === template.nextDueDateCalculation)?.label || template.nextDueDateCalculation;
    };

    const attachmentCount = Object.keys(template.instructionalAttachments || {}).length;

    return (
        <div className="template-detail-pane">
            {/* Header */}
            <div className="template-detail-header">
                <div className="template-detail-title-row">
                    <div>
                        <h3 className="template-detail-name">{template.name}</h3>
                        <div className="d-flex gap-2 flex-wrap">
                            <Badge bg="secondary">
                                {translateInspectionCategory(template.category || InspectionCategory.MeterReadings)}
                            </Badge>
                            {template.regulatoryType && (
                                <Badge bg="warning" text="dark" className="badge-myndighetskrav">
                                    {translateRegulatoryType(template.regulatoryType)}
                                </Badge>
                            )}
                            {template.system && (
                                <Badge bg="light" text="dark" title="Systemmall: kan inte tas bort, kategori och komponentgrupp är låsta">
                                    Systemmall
                                </Badge>
                            )}
                        </div>
                    </div>
                    <div className="template-detail-actions">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onEdit(template)}
                        >
                            <Pencil size={14} className="me-1" />
                            Redigera
                        </Button>
                        {!template.system && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => onDelete(template)}
                            >
                                <Trash2 size={14} className="me-1" />
                                Radera
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Description */}
            <div className="template-detail-card">
                <h6 className="template-detail-card-title">Beskrivning</h6>
                <p className="template-detail-description">{template.description}</p>
            </div>

            {template.componentSpecificType && (
                <div className="template-detail-card">
                    <h6 className="template-detail-card-title">Föreslagen komponenttyp</h6>
                    <div className="template-detail-description">
                        {getTranslation(template.componentSpecificType)}
                    </div>
                </div>
            )}

            {template.componentBaseTypeGroup && (
                <div className="template-detail-card">
                    <h6 className="template-detail-card-title">Kopplad komponentgrupp</h6>
                    <div className="template-detail-description">
                        <div>{ASSESSMENT_GROUPS[template.componentBaseTypeGroup].label}</div>
                        <div className="text-muted small">{ASSESSMENT_GROUPS[template.componentBaseTypeGroup].membersDescription}</div>
                        <div className="text-muted small mt-1">Kopplas automatiskt till alla aktiva komponenter i gruppen i varje byggnad. Bedömningen utförs i appen.</div>
                    </div>
                </div>
            )}

            {/* Schedule */}
            <div className="template-detail-card">
                <h6 className="template-detail-card-title">Schema</h6>
                <div className="template-detail-grid">
                    <div className="template-detail-item">
                        <div className="template-detail-item-icon">
                            <Clock size={18} />
                        </div>
                        <div className="template-detail-item-info">
                            <div className="template-detail-item-label">Intervall</div>
                            <div className="template-detail-item-value">{getIntervalLabel()}</div>
                        </div>
                    </div>
                    <div className="template-detail-item">
                        <div className="template-detail-item-icon">
                            <Clock size={18} />
                        </div>
                        <div className="template-detail-item-info">
                            <div className="template-detail-item-label">Nästa datum beräknas</div>
                            <div className="template-detail-item-value">{getNextDueDateLabel()}</div>
                        </div>
                    </div>
                    {template.advanceNotice > 0 && (
                        <div className="template-detail-item">
                            <div className="template-detail-item-icon">
                                <Clock size={18} />
                            </div>
                            <div className="template-detail-item-info">
                                <div className="template-detail-item-label">Förvarning</div>
                                <div className="template-detail-item-value">{template.advanceNotice} dagar</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Estimates */}
            {(template.estimatedTime > 0 || template.estimatedCost > 0) && (
                <div className="template-detail-card">
                    <h6 className="template-detail-card-title">Uppskattningar</h6>
                    <div className="template-detail-grid">
                        {template.estimatedTime > 0 && (
                            <div className="template-detail-item">
                                <div className="template-detail-item-info">
                                    <div className="template-detail-item-label">Arbetstid</div>
                                    <div className="template-detail-item-value">{template.estimatedTime} minuter</div>
                                </div>
                            </div>
                        )}
                        {template.estimatedCost > 0 && (
                            <div className="template-detail-item">
                                <div className="template-detail-item-info">
                                    <div className="template-detail-item-label">Kostnad</div>
                                    <div className="template-detail-item-value">{template.estimatedCost} kr</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Measurement */}
            {template.takesMeasurement && (
                <div className="template-detail-card">
                    <h6 className="template-detail-card-title">Mätvärde</h6>
                    <div className="template-detail-grid">
                        <div className="template-detail-item">
                            <div className="template-detail-item-icon">
                                <Ruler size={18} />
                            </div>
                            <div className="template-detail-item-info">
                                <div className="template-detail-item-label">Enhet</div>
                                <div className="template-detail-item-value">{template.takesMeasurement.metric}</div>
                            </div>
                        </div>
                        {template.takesMeasurement.acceptableRange && (
                            <div className="template-detail-item">
                                <div className="template-detail-item-info">
                                    <div className="template-detail-item-label">Acceptabelt intervall</div>
                                    <div className="template-detail-item-value">
                                        {template.takesMeasurement.acceptableRange.min !== undefined && (
                                            <span>Min: {template.takesMeasurement.acceptableRange.min} </span>
                                        )}
                                        {template.takesMeasurement.acceptableRange.max !== undefined && (
                                            <span>Max: {template.takesMeasurement.acceptableRange.max}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Requirements - only show if there are actual requirements */}
            {template.photoRequired && (
                <div className="template-detail-card">
                    <h6 className="template-detail-card-title">Krav</h6>
                    <div className="template-detail-badges">
                        <Badge bg="success">
                            <Camera size={12} className="me-1" />
                            Foto krävs
                        </Badge>
                    </div>
                </div>
            )}

            {/* Attachments */}
            {attachmentCount > 0 && (
                <div className="template-detail-card">
                    <h6 className="template-detail-card-title">Bilagor</h6>
                    <div className="template-attachments">
                        {Object.entries(template.instructionalAttachments || {}).map(([key, attachment]) => (
                            <div key={key} className="template-attachment-item">
                                <FileText size={16} className="me-2 text-muted" />
                                <span>{attachment.key}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BuildingCheckTemplateDetailPane;
